import Hls from "hls.js";
import { useEffect, useRef, type RefObject } from "react";

/**
 * Attaches an HLS (`.m3u8`) stream to a `<video>` element via hls.js, or the
 * browser's native HLS support on Safari/iOS. Falls back to `onUnsupported`
 * (which the caller uses to swap in the poster image) if neither is
 * available.
 *
 * `shouldPlay` is separate from `src` so a caller can attach the stream
 * early and leave it paused: the element decodes and paints frame 0 while
 * the rest buffers, giving the tile a real first-frame still to show
 * instead of an unrelated placeholder. Toggling `shouldPlay` starts and
 * stops playback without tearing the stream down.
 */
export const useHlsVideoSource = (
  videoRef: RefObject<HTMLVideoElement | null>,
  src: string,
  onUnsupported: () => void,
  shouldPlay = true
): void => {
  const shouldPlayRef = useRef(shouldPlay);

  useEffect(() => {
    shouldPlayRef.current = shouldPlay;
  }, [shouldPlay]);

  useEffect(() => {
    const video = videoRef.current;
    // An empty `src` means the caller intentionally isn't showing video yet
    // (e.g. the "Video on hover" variant before the pointer enters the
    // tile) — not a real playback failure, so don't call `onUnsupported`.
    if (!video || !src) return undefined;

    // Autoplay can be interrupted by the browser (e.g. React StrictMode's
    // dev-only double-effect mount/cleanup/remount cycle, or the tile
    // scrolling offscreen) — this rejection is expected and safe to ignore;
    // left unhandled, it surfaces as a noisy "unhandled rejection" overlay.
    const playSafely = (): void => {
      // A preloaded-but-inactive video must stay parked on frame 0.
      if (!shouldPlayRef.current) return;
      video.play().catch(() => {});
    };

    if (Hls.isSupported()) {
      // Keep the whole buffer around (these are short preview clips, so the
      // memory cost is negligible). Without this, hls.js evicts segments
      // behind the playhead as it advances, which would make a manual
      // restart-from-0 loop (below) re-fetch data unnecessarily.
      const hls = new Hls({ backBufferLength: Infinity });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, playSafely);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) onUnsupported();
      });

      // The native `loop` attribute is unreliable on MediaSource-backed
      // (hls.js) elements — Chromium sometimes stalls on the final frame
      // (`currentTime === duration`, `paused: false`, `ended: false`)
      // instead of restarting. Disable native looping and restart manually
      // on `ended`/`timeupdate` so playback always resumes.
      video.loop = false;
      const restart = (): void => {
        video.currentTime = 0;
        playSafely();
      };
      const handleTimeUpdate = (): void => {
        if (video.duration > 0 && video.currentTime >= video.duration - 0.05) {
          restart();
        }
      };
      video.addEventListener("ended", restart);
      video.addEventListener("timeupdate", handleTimeUpdate);
      return () => {
        video.removeEventListener("ended", restart);
        video.removeEventListener("timeupdate", handleTimeUpdate);
        hls.destroy();
      };
    }

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.addEventListener("loadedmetadata", playSafely);
      return undefined;
    }

    onUnsupported();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onUnsupported is a setState setter passed fresh each render
  }, [src, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (shouldPlay && src) {
      video.play().catch(() => {});
      return;
    }
    // Rewinding on deactivate means the next activation — and the paused
    // still shown in the meantime — starts from frame 0 rather than
    // wherever the previous run left off.
    video.pause();
    video.currentTime = 0;
  }, [shouldPlay, src, videoRef]);
};
